// src/services/storage/universalDirectoryManager.ts

import { storage, db } from "../../lib/firebase";
import {
  ref,
  listAll,
  uploadBytes,
  deleteObject,
  getMetadata,
} from "firebase/storage";
import {
  doc,
  setDoc,
  collection,
  serverTimestamp,
  deleteDoc,
  getDocs,
  query,
  limit,
} from "firebase/firestore";
import { useDebugStore } from "../../store/debugStore";
import { FileNaming } from "../../utils/UniversalFileNaming";

export type StorageType = "firebase" | "database";
export type ContentType =
  | "vcard"
  | "qrcode"
  | "profile"
  | "smarttag"
  | "snowgear"
  | "user"
  | "svg";

interface DirectoryStructure {
  base: string;
  required: string[];
  userSubfolders: string[];
  categoryFolders: Record<string, string[]>;
  documents: Record<string, Record<string, string>>;
  fileExtensions: Record<string, string>;
  firestoreCollections: string[];
}

export interface VerificationResult {
  success: boolean;
  paths: Record<string, string>;
  collections: Record<string, boolean>;
  error?: string;
}

export class UniversalDirectoryManager {
  public static readonly STRUCTURE: DirectoryStructure = {
    base: "users",
    required: [],
    userSubfolders: ["profile", "qrcodesMetadata", "vcards", "svg"],
    categoryFolders: {
      qrcodes: [
        "business",
        "extremeKit",
        "home",
        "luggage",
        "myTech",
        "snowGear_boots",
        "snowGear_glovesApparel",
        "snowGear_helmetMask",
        "snowGear_snowboards",
      ],
      qrcodesMetadata: [
        "business",
        "extremeKit",
        "home",
        "luggage",
        "myTech",
        "snowGear_boots",
        "snowGear_glovesApparel",
        "snowGear_helmetMask",
        "snowGear_snowboards",
      ],
      vcards: [
        "business",
        "extremeKit",
        "home",
        "luggage",
        "myTech",
        "snowGear",
        "snowGear_boots",
        "snowGear_glovesApparel",
        "snowGear_helmetMask",
        "snowGear_snowboards",
      ],
      svg: [
        "business",
        "extremeKit",
        "home",
        "luggage",
        "myTech",
        "snowGear_boots",
        "snowGear_glovesApparel",
        "snowGear_helmetMask",
        "snowGear_snowboards",
      ],
    },
    documents: {
      profile: {
        data: "data",
      },
    },
    fileExtensions: {
      qrcodes: ".svg",
      qrcodesMetadata: ".json",
      vcards: ".vcf",
      svg: ".svg",
    },
    firestoreCollections: [".collection"],
  };

  private static readonly PLACEHOLDER_CONTENT = new Uint8Array([]);

  /**
   * Generates a standardized path for any content type
   */
  static getPath(params: {
    userId: string;
    contentType: ContentType;
    category?: string;
    state?: "active" | "repository";
    isMetadata?: boolean;
    // NEW: If you want to add 'document' explicitly to the method signature:
    document?: string;
  }): string {
    const { userId, contentType, category, isMetadata, document } = params;
    const addDebugMessage = useDebugStore.getState().addMessage;

    if (!userId) {
      throw new Error("userId is required");
    }

    if (!contentType) {
      throw new Error("contentType is required");
    }

    if (!this.isValidContentType(contentType)) {
      throw new Error(`Invalid content type: ${contentType}`);
    }

    const basePath = `${this.STRUCTURE.base}/${userId}`;

    try {
      let resultPath: string;
      switch (contentType) {
        case "user":
          resultPath = basePath;
          break;

        case "profile":
          resultPath = `${basePath}/profile`;
          break;

        case "vcard":
          resultPath = `${basePath}/vcards`;
          if (category) {
            if (!this.STRUCTURE.categoryFolders.vcards.includes(category)) {
              throw new Error(`Invalid vCard category: ${category}`);
            }
            resultPath += `/${category}`;
          }
          break;

        case "qrcode":
          const qrBase = isMetadata ? "qrcodesMetadata" : "qrcodes";
          resultPath = `${basePath}/${qrBase}`;
          if (category) {
            if (!this.STRUCTURE.categoryFolders.qrcodes.includes(category)) {
              throw new Error(`Invalid QR code category: ${category}`);
            }
            resultPath += `/${category}`;
          }
          break;

        case "smarttag":
          resultPath = `${basePath}/smarttags`;
          if (category) {
            resultPath += `/${category}`;
          }
          break;

        case "snowgear":
          resultPath = `${basePath}/snowgear`;
          if (category) {
            if (!this.STRUCTURE.categoryFolders.snowgear?.includes(category)) {
              throw new Error(`Invalid snowgear category: ${category}`);
            }
            resultPath += `/${category}`;
          }
          break;

        case "svg":
          resultPath = `${basePath}/svg`;
          if (category) {
            if (!this.STRUCTURE.categoryFolders.svg.includes(category)) {
              throw new Error(`Invalid SVG category: ${category}`);
            }
            resultPath += `/${category}`;
          }
          break;

        default:
          throw new Error(`Unknown content type: ${contentType}`);
      }

      // Append document if provided (normalize profile metadata naming)
      if (document) {
        const normalizedDocument =
          document === "collection"
            ? ".collection"
            : document === "data"
              ? "data"
              : document;
        resultPath += `/${normalizedDocument}`;
      }

      addDebugMessage("info", `Generated path: ${resultPath}`);
      return resultPath;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      addDebugMessage("error", `Path generation failed: ${errorMessage}`);
      throw error;
    }
  }

  /**
   * Validates a content type
   */
  private static isValidContentType(
    contentType: any,
  ): contentType is ContentType {
    return [
      "vcard",
      "qrcode",
      "profile",
      "smarttag",
      "snowgear",
      "user",
      "svg",
    ].includes(contentType);
  }

  /**
   * Validates a filename using the centralized FileNaming utility
   */
  static validateFileName(params: {
    userId: string;
    contentType: ContentType;
    category?: string;
    fileName: string;
  }): boolean {
    const { contentType, category, fileName } = params;
    const addDebugMessage = useDebugStore.getState().addMessage;

    // Validate using FileNaming utility (checks pattern, timestamp, uniqueId, extension, etc.)
    const isValid = FileNaming.validate(fileName);

    if (!isValid) {
      addDebugMessage("error", `Filename validation failed for: ${fileName}`);
      return false;
    }

    // (Optional) Check that the category is valid for this contentType
    if (
      category &&
      !this.STRUCTURE.categoryFolders[contentType]?.includes(category)
    ) {
      addDebugMessage(
        "error",
        `Invalid category for ${contentType}: ${category}`,
      );
      return false;
    }

    addDebugMessage("success", `Filename is valid: ${fileName}`);
    return true;
  }

  /**
   * Verifies if a directory exists and creates it if it doesn't.
   * For Firebase Storage, only creates directories for QR code images and SVG files.
   * For Firestore, creates collections for metadata and user data.
   */
  static async verifyDirectory(
    path: string,
    contentType?: ContentType,
  ): Promise<boolean> {
    const addDebugMessage = useDebugStore.getState().addMessage;
    try {
      addDebugMessage("info", `🔍 Verifying directory: ${path}`);

      // For Firebase Storage directories (QR codes and SVGs)
      if (
        (contentType === "qrcode" || contentType === "svg") &&
        !path.includes("Metadata")
      ) {
        const placeholderPath = `${path}/.placeholder`;
        const placeholderRef = ref(storage, placeholderPath);

        try {
          // Check if .placeholder exists
          await getMetadata(placeholderRef);
          addDebugMessage(
            "info",
            `✅ Directory exists with .placeholder at: ${path}`,
          );
          return true;
        } catch (readError) {
          try {
            // Create .placeholder if it doesn't exist
            await uploadBytes(placeholderRef, this.PLACEHOLDER_CONTENT, {
              contentType: "text/plain",
              customMetadata: {
                purpose: "directory_marker",
              },
            });
            addDebugMessage(
              "info",
              `✅ Created directory with .placeholder at: ${path}`,
            );
            return true;
          } catch (writeError: any) {
            // Handle concurrent creation (412 Precondition Failed)
            if (
              writeError?.status_ === 412 ||
              (writeError?.code === "storage/unknown" &&
                writeError.message?.includes("412"))
            ) {
              addDebugMessage(
                "info",
                `ℹ️ Directory already exists with .placeholder at: ${path}`,
              );
              return true;
            }
            throw writeError;
          }
        }
      }

      // For Firestore collections
      if (
        this.STRUCTURE.firestoreCollections.some((collection) =>
          path.endsWith(collection),
        )
      ) {
        const collectionRef = collection(db, path);
        await setDoc(doc(collectionRef, ".collection"), {
          created: serverTimestamp(),
        });
        addDebugMessage("info", `✅ Created Firestore collection at: ${path}`);
      }

      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      addDebugMessage(
        "error",
        `❌ Directory verification failed at ${path}: ${errorMessage}`,
      );
      throw error;
    }
  }

  /**
   * Checks whether a directory path exists (Firestore collection or Storage prefix).
   * - For Firestore collections: returns true if at least one document exists.
   * - For Storage prefixes: returns true if listAll succeeds (even if empty).
   */
  static async verifyDirectoryExists(path: string): Promise<boolean> {
    if (!path) return false;

    try {
      // Treat Firestore collections explicitly
      if (
        path.includes("/qrcodesMetadata") ||
        path.includes("/vcards") ||
        path.includes("/profile")
      ) {
        const snapshot = await getDocs(query(collection(db, path), limit(1)));
        return !snapshot.empty;
      }

      // Default to Storage prefix check
      const storageRef = ref(storage, path);
      await listAll(storageRef);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Deletes a directory
   */
  static async deleteDirectory(path: string): Promise<void> {
    const addDebugMessage = useDebugStore.getState().addMessage;
    addDebugMessage("info", `Deleting directory: ${path}`);

    try {
      // Try to delete from both Storage and Firestore
      const directoryRef = ref(storage, path);
      const docRef = doc(db, path, ".collection");

      await Promise.all([
        listAll(directoryRef).then((result) => {
          const deletePromises = result.items.map((item) =>
            deleteObject(ref(storage, item.fullPath)),
          );
          return Promise.all(deletePromises);
        }),
        deleteDoc(docRef),
      ]);

      addDebugMessage("success", `Directory deleted: ${path}`);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      addDebugMessage("error", `Failed to delete directory: ${errorMessage}`);
      throw error;
    }
  }

  /**
   * Verifies and creates the basic user structure
   */
  static async verifyUserStructure(
    userId: string,
  ): Promise<VerificationResult> {
    const addDebugMessage = useDebugStore.getState().addMessage;
    addDebugMessage(
      "info",
      `Starting user structure verification for: ${userId}`,
    );

    const paths: Record<string, string> = {};
    const collections: Record<string, boolean> = {};
    const result: VerificationResult = {
      success: true,
      paths,
      collections,
    };

    try {
      // 1. Verify base user directory
      const basePath = `${this.STRUCTURE.base}/${userId}`;
      addDebugMessage("info", `Verifying base directory: ${basePath}`);
      const baseSuccess = await this.verifyDirectory(basePath);
      paths.base = basePath;
      collections.base = baseSuccess;
      if (!baseSuccess) {
        throw new Error("Failed to verify base user directory");
      }

      // 2. Verify user subfolders
      for (const folder of this.STRUCTURE.userSubfolders) {
        const path = `${basePath}/${folder}`;
        addDebugMessage("info", `Verifying subfolder: ${folder}`);
        paths[folder] = path;
        const success = await this.verifyDirectory(path);
        collections[folder] = success;
        if (!success) {
          addDebugMessage("error", `Failed to verify subfolder: ${folder}`);
          result.success = false;
        }
      }

      // 3. Verify category folders
      for (const [parent, categories] of Object.entries(
        this.STRUCTURE.categoryFolders,
      )) {
        addDebugMessage("info", `Verifying categories for: ${parent}`);
        for (const category of categories) {
          let path;
          if (parent === "qrcodes") {
            path = this.getPath({ userId, contentType: "qrcode", category });
          } else if (parent === "qrcodesMetadata") {
            path = this.getPath({
              userId,
              contentType: "qrcode",
              category,
              isMetadata: true,
            });
          } else if (parent === "vcards") {
            path = this.getPath({ userId, contentType: "vcard", category });
          } else if (parent === "svg") {
            path = this.getPath({ userId, contentType: "svg", category });
          } else {
            path = `${basePath}/${parent}/${category}`;
          }

          const key = `${parent}_${category.replace("/", "_")}`;
          addDebugMessage("info", `Verifying category: ${category}`);
          paths[key] = path;
          const success = await this.verifyDirectory(path);
          collections[key] = success;
          if (!success) {
            addDebugMessage("error", `Failed to verify category: ${category}`);
            result.success = false;
          }
        }
      }

      if (!result.success) {
        throw new Error("Some directories failed to verify");
      }

      addDebugMessage("success", "User structure verified successfully", {
        paths,
        collections,
      });

      return result;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      addDebugMessage("error", "User structure verification failed", {
        error: errorMessage,
        paths,
        collections,
      });
      return {
        ...result,
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Gets all content paths for a specific content type
   */
  static async getContentPaths(
    userId: string,
    contentType: ContentType,
  ): Promise<{
    success: boolean;
    paths: { metadata: string; content: string };
    error?: string;
  }> {
    try {
      const metadataPath = this.getPath({
        userId,
        contentType,
        isMetadata: true,
      });

      const contentPath = this.getPath({
        userId,
        contentType,
        isMetadata: false,
      });

      return {
        success: true,
        paths: {
          metadata: metadataPath,
          content: contentPath,
        },
      };
    } catch (error) {
      return {
        success: false,
        paths: {
          metadata: "",
          content: "",
        },
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Important notes about the directory structure:
   * 1. All paths are relative to the storage root
   * 2. Firebase Storage and Firestore have different directory structures
   * 3. Each storage type has its own verification and creation methods
   * 4. Directory structures are maintained separately but parallel
   * 5. Always use UniversalDirectoryManager methods to interact with either storage
   */
}
